import React, { useContext } from 'react'
import { PagingNav } from './paging-nav'
import {
  VulnerabilitiesGridContextValue,
  VulnerabilitiesGridContextValueData
} from '../vulnerabilities-grid/grid'
import { RouteContext } from '../route-provider'
import { updateRoute } from '../route'
import {
  SinkGroup,
  VulnerabilityOrSinkGroup
} from '../vulnerabilities-grid/shared'

interface PagingNavWrapperProps<TAggregationCategory extends string> {
  getCurrentIdFromRoute: (routeParameters: string[]) => string | undefined
  getDrilldownPath: (data: {
    organizationID: string
    id: string
    routeParameters: string[]
  }) => string
  getGridPath: (data: {
    organizationID: string
    routeParameters: string[]
  }) => string
  data?: Pick<
    VulnerabilitiesGridContextValueData<TAggregationCategory, {}>,
    | 'totalRowCount'
    | 'loadMore'
    | 'rowIndexFinder'
    | 'rowGetter'
    | 'loadedRowCount'
    | 'organizationID'
  >
}

interface CreateWrappedPagingNavArgs<
  TOrderField extends string,
  TAggregationCategory extends string
> extends Omit<PagingNavWrapperProps<TOrderField>, 'data'> {
  GridContext: React.Context<
    VulnerabilitiesGridContextValue<TOrderField, TAggregationCategory>
  >
}

export function createWrappedPagingNav<
  TOrderField extends string,
  TAggregationCategory extends string
>({
  GridContext,
  getCurrentIdFromRoute,
  getDrilldownPath,
  getGridPath
}: CreateWrappedPagingNavArgs<TOrderField, TAggregationCategory>) {
  return function WrappedPagingNav() {
    return (
      <GridContext.Consumer>
        {({ data }) => {
          return (
            <PagingNavWrapper<TAggregationCategory>
              data={data}
              getCurrentIdFromRoute={getCurrentIdFromRoute}
              getDrilldownPath={getDrilldownPath}
              getGridPath={getGridPath}
            />
          )
        }}
      </GridContext.Consumer>
    )
  }
}

export function PagingNavWrapper<TAggregationCategory extends string>({
  getCurrentIdFromRoute,
  getDrilldownPath,
  getGridPath,
  data
}: PagingNavWrapperProps<TAggregationCategory>) {
  const {
    parameters: routeParameters,
    getQueryParamsAsString,
    queryParameters,
    convertQueryParamMapToString
  } = useContext(RouteContext)

  if (!data) {
    return <></>
  }

  const {
    totalRowCount,
    loadMore,
    rowIndexFinder,
    rowGetter,
    loadedRowCount,
    organizationID
  } = data

  const id = getCurrentIdFromRoute(routeParameters)
  if (!id) {
    return <></>
  }

  const rowIndex = rowIndexFinder((row) => row.vulnerability?.id === id)

  if (rowIndex < 0) {
    return <></>
  }

  // If there are more rows to load and the current row is
  // close to the end of the loaded set, load more in advance
  const lastLoadedRowIndex = loadedRowCount - 1
  if (totalRowCount > loadedRowCount && rowIndex > lastLoadedRowIndex - 2) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadMore()
  }

  const previousRow: VulnerabilityOrSinkGroup | undefined =
    rowIndex > 0 ? rowGetter({ index: rowIndex - 1 }) : undefined
  const nextRow: VulnerabilityOrSinkGroup | undefined =
    rowIndex < loadedRowCount ? rowGetter({ index: rowIndex + 1 }) : undefined
  const isPreviousRowSinkGroup = previousRow && !!previousRow?.sinkGroup
  const isNextRowSinkGroup = nextRow && !!nextRow?.sinkGroup

  const getSinkGroupHash = (sinkGroup: SinkGroup) => {
    const { sinkHash, ruleName } = sinkGroup

    queryParameters.delete('groupedBy')
    queryParameters.set('sinks', [sinkHash])
    queryParameters.set('types', [ruleName])
    queryParameters.set('drilldown', ['true'])
    return `${getGridPath({
      organizationID,
      routeParameters
    })}?${convertQueryParamMapToString(queryParameters)}`
  }

  function previous() {
    if (isPreviousRowSinkGroup) {
      updateRoute(getSinkGroupHash(previousRow.sinkGroup))
    } else {
      const qs = getQueryParamsAsString()
      const queryParms = qs ? `?${qs}` : ''
      updateRoute(
        `${getDrilldownPath({
          organizationID,
          id: rowGetter({ index: rowIndex - 1 }).vulnerability.id,
          routeParameters
        })}${queryParms}`
      )
    }
  }

  function next() {
    if (isNextRowSinkGroup) {
      updateRoute(getSinkGroupHash(nextRow.sinkGroup))
    } else {
      const qs = getQueryParamsAsString()
      const queryParms = qs ? `?${qs}` : ''
      updateRoute(
        `${getDrilldownPath({
          organizationID,
          id: rowGetter({ index: rowIndex + 1 }).vulnerability.id,
          routeParameters
        })}${queryParms}`
      )
    }
  }

  return (
    <PagingNav
      current={rowIndex + 1}
      total={totalRowCount}
      previous={rowIndex > 0 ? previous : undefined}
      next={rowIndex < loadedRowCount - 1 ? next : undefined}
      previousSinkGroupTitle={
        isPreviousRowSinkGroup ? previousRow.sinkGroup.title : undefined
      }
      nextSinkGroupTitle={
        isNextRowSinkGroup ? nextRow.sinkGroup.title : undefined
      }
    />
  )
}
